import { useQuery } from '@tanstack/react-query';
import { useIsEventbritePlusEnabled } from '@/bsp/config';
import { useUserData } from '@/hooks/useUserData';

const STALE_MS = 5 * 60 * 1000;

type SubscriptionResponse = {
  isSubscribed?: boolean;
  hasEarlyAccessPurchaseWindows?: boolean;
};

export type UserSubscriptionLookup = {
  isSubscribed: boolean;
  hasEarlyAccessPurchaseWindows: boolean;
  isSubscriptionReady: boolean;
};

async function fetchUserSubscription(): Promise<SubscriptionResponse> {
  const response = await fetch('/e/api/user/subscription', { credentials: 'include' });
  if (!response.ok) {
    throw new Error(`User subscription request failed with status ${response.status}`);
  }

  return (await response.json()) as SubscriptionResponse;
}

export function useUserSubscription(): UserSubscriptionLookup {
  const { isEnabled: isEventbritePlusEnabled } = useIsEventbritePlusEnabled();
  const { userId, isLoading: isUserDataLoading } = useUserData();
  const enabled = !!userId && isEventbritePlusEnabled;
  const { data, isFetched } = useQuery({
    queryKey: ['user-subscription', userId ?? 'anonymous'],
    queryFn: fetchUserSubscription,
    enabled,
    staleTime: STALE_MS,
    retry: false,
  });

  if (!isEventbritePlusEnabled || (!isUserDataLoading && !userId)) {
    return {
      isSubscribed: false,
      hasEarlyAccessPurchaseWindows: false,
      isSubscriptionReady: true,
    };
  }

  return {
    isSubscribed: data?.isSubscribed ?? false,
    hasEarlyAccessPurchaseWindows: data?.hasEarlyAccessPurchaseWindows ?? false,
    isSubscriptionReady: !isUserDataLoading && isFetched,
  };
}
